import {Edge} from "reactflow";
import {NodesAndEdges, Position, SmartTriggerProcessorNodeConfiguration, SmartTriggerUiConfiguration} from "../types";
import {
    createEdge,
    createEdgeId,
    findEdge,
    findNextNodes,
    findNode,
    findPositionDifference,
    findProcessorNode,
    generateNewProcessorNode,
    toNodeMap
} from "../service/smartTriggerService";
import {ArrayUtils} from "../../../utils/arrayUtils";


export enum SmartTriggerConfigurationActionType {
    ADD_NODE = "add-node",
    UPDATE_NODE = "update-node",
    UPDATE_NODES = "update-nodes",
    UPDATE_EDGES = "update-edges",
    UPDATE_CONFIGURATION = "update-configuration",
    REMOVE_NODE = "remove-node",
}

type AddNodeAction = {
    // payload: {
    //     previousNode: {
    //         id: string,
    //         width: number,
    //         height: number
    //     }
    //     nextNode: string
    //     type: string
    // },
    payload: SmartTriggerProcessorNodeConfiguration
    type: SmartTriggerConfigurationActionType.ADD_NODE
}

type UpdateNodeAction = {
    payload: SmartTriggerProcessorNodeConfiguration
    type: SmartTriggerConfigurationActionType.UPDATE_NODE
}

type UpdateNodesAction = {
    payload: {
        nodes: SmartTriggerProcessorNodeConfiguration[]
    }
    type: SmartTriggerConfigurationActionType.UPDATE_NODES
}

type UpdateEdgesAction = {
    payload: {
        edges: Edge[]
    },
    type: SmartTriggerConfigurationActionType.UPDATE_EDGES
}

type UpdateConfigurationAction = {
    payload: Partial<SmartTriggerUiConfiguration>
    type: SmartTriggerConfigurationActionType.UPDATE_CONFIGURATION
}

type RemoveNodeAction = {
    payload: {
        id: string
    },
    type: SmartTriggerConfigurationActionType.REMOVE_NODE
}

export type SmartTriggerConfigurationAction =
    AddNodeAction
    | UpdateNodesAction
    | UpdateEdgesAction
    | UpdateConfigurationAction
    | RemoveNodeAction
    | UpdateNodeAction

export default function smartTriggerConfigurationReducer(configuration: SmartTriggerUiConfiguration, action: SmartTriggerConfigurationAction): SmartTriggerUiConfiguration {
    if (action.type === SmartTriggerConfigurationActionType.UPDATE_NODES) {
        return {...configuration, nodes: action.payload.nodes}
    }
    if (action.type === SmartTriggerConfigurationActionType.UPDATE_EDGES) {
        return {...configuration, edges: action.payload.edges}
    }
    if (action.type === SmartTriggerConfigurationActionType.UPDATE_CONFIGURATION) {
        return {...configuration, ...action.payload}
    }
    if (action.type === SmartTriggerConfigurationActionType.ADD_NODE) {
        const nodesAndEdges = addNewNode(configuration, action)
        return {...configuration, ...nodesAndEdges};
    }
    if (action.type === SmartTriggerConfigurationActionType.REMOVE_NODE) {
        const nodesAndEdges = removeNode(configuration, action.payload.id)
        return {...configuration, ...nodesAndEdges};
    }
    if (action.type === SmartTriggerConfigurationActionType.UPDATE_NODE) {
        const newNodes = configuration.nodes.map(node => {
            if (node.id === action.payload.id) {
                return {...node, ...action.payload}
            }
            return node;
        })
        return {...configuration, nodes: newNodes}

    }
    return configuration;
}

function removeLastNode(configuration: SmartTriggerUiConfiguration, nodeToRemove: SmartTriggerProcessorNodeConfiguration, nodesAndEdges: NodesAndEdges): NodesAndEdges {
    const previousNode = findProcessorNode(nodeToRemove.data.previous as string, nodesAndEdges.nodes);
    previousNode.data.next = null;
    nodesAndEdges.nodes = nodesAndEdges.nodes.filter(node => node.id !== nodeToRemove.id)
    const edgesToRemove: string[] = []
    edgesToRemove.push(createEdgeId(nodeToRemove.data.previous as string, nodeToRemove.id));
    nodesAndEdges.edges = nodesAndEdges.edges.filter(edge => !edgesToRemove.includes(edge.id))
    return nodesAndEdges;
}


function removeNode(configuration: SmartTriggerUiConfiguration, id: string): NodesAndEdges {
    const nodesAndEdges = cloneNodesAndEdges(configuration);
    const nodeToRemove = findProcessorNode(id, nodesAndEdges.nodes);
    if (!nodeToRemove.data.next) {
        return removeLastNode(configuration, nodeToRemove, nodesAndEdges);
    }
    const previousNode = findProcessorNode(nodeToRemove.data.previous as string, nodesAndEdges.nodes);
    const nextNode = findProcessorNode(nodeToRemove.data.next as string, nodesAndEdges.nodes);
    previousNode.data.next = nextNode.id;
    nextNode.data.previous = previousNode.id
    nodesAndEdges.nodes = nodesAndEdges.nodes.filter(node => node.id !== id)
    nodesAndEdges.edges.push(createEdge(nodeToRemove.data.previous as string, nodeToRemove.data.next as string))
    const edgesToRemove: string[] = []
    edgesToRemove.push(createEdgeId(nodeToRemove.data.previous as string, nodeToRemove.id));
    edgesToRemove.push(createEdgeId(nodeToRemove.id, nodeToRemove.data.next as string))
    nodesAndEdges.edges = nodesAndEdges.edges.filter(edge => !edgesToRemove.includes(edge.id))
    console.log("nodesAndEdges", nodesAndEdges)

    //update new positions to the next nodes
    const positionDifferences = findPositionDifference(nodeToRemove, nextNode);
    positionDifferences.y = -positionDifferences.y
    positionDifferences.x = -positionDifferences.x
    updatePositions(previousNode, toNodeMap(nodesAndEdges.nodes), positionDifferences);
    return nodesAndEdges;
}

function cloneNodesAndEdges(configuration: SmartTriggerUiConfiguration): NodesAndEdges {
    return {
        nodes: [...configuration.nodes],
        edges: [...configuration.edges]
    }
}

function updatePositions(nextNode: SmartTriggerProcessorNodeConfiguration, nodeMap: Record<string, SmartTriggerProcessorNodeConfiguration>, positionDifference: Position) {
    const nextNodes = findNextNodes(nextNode, nodeMap);
    if (ArrayUtils.isNotEmpty(nextNodes)) {
        nextNodes.forEach(node => {
            const newPosition = {...node.position};
            newPosition.y = newPosition.y + positionDifference.y;
            node.position = newPosition;
            updatePositions(node, nodeMap, positionDifference);
        })
    }
}

function addNewNode(configuration: SmartTriggerUiConfiguration, action: AddNodeAction): NodesAndEdges {
    const nodesAndEdges = cloneNodesAndEdges(configuration);
    const nodeMap = toNodeMap(nodesAndEdges.nodes);
    // create new node and change the previous and next nodes
    const parentNode = findNode(action.payload.data.previous as string, nodeMap);
    const nextNode = findNode(action.payload.data.next as string, nodeMap);
    const newNode = generateNewProcessorNode(parentNode, nextNode, action.payload);
    nodeMap[newNode.id] = newNode;
    nodesAndEdges.nodes.push(newNode);
    parentNode.data.next = newNode.id;
    nextNode.data.previous = newNode.id
    //update new positions to the next nodes
    const positionDifferences = findPositionDifference(parentNode, nextNode);
    updatePositions(newNode, nodeMap, positionDifferences);

    //set Edges
    const previousEdge = findEdge(parentNode.id, nextNode.id, nodesAndEdges.edges);
    previousEdge.id = createEdgeId(parentNode.id, newNode.id);
    previousEdge.target = newNode.id;
    previousEdge.type = 'buttonEdge'
    const nextEdge = createEdge(newNode.id, nextNode.id);
    nodesAndEdges.edges.push(nextEdge)
    return nodesAndEdges;
}
